import {
  BoardState,
  Piece,
  Square,
  Color,
  PieceType,
  GameStatus,
  GameVariant,
  GameMode,
  DEFAULT_GAME_MODE,
  INTERNATIONAL_CONFIG,
  VARIANT_CONFIGS,
} from '@draughts/shared';
import { serializePosition } from '../game/position-hash.js';

/**
 * Crea lo stato iniziale della scacchiera per una variante.
 *
 * `gameMode` è un modificatore ortogonale della condizione di vittoria (normal /
 * breakthrough / antidraughts): la posizione iniziale è identica, cambia solo
 * come `determineGameStatus` interpreta lo stato terminale.
 */
export function createInitialBoard(
  variant: GameVariant = GameVariant.INTERNATIONAL,
  ruleMode: GameMode = DEFAULT_GAME_MODE
): BoardState {
  const config = VARIANT_CONFIGS[variant];
  /*
   * Il modo tipico di sbagliare non e' chiedere una variante che non esiste: e'
   * passare la forma da FILO ('international', minuscola, quella di
   * `ChallengeSettings`) dove va l'enum `GameVariant` ('INTERNATIONAL'). Senza
   * questo controllo la chiave mancante diventa un `Cannot read properties of
   * undefined (reading 'initialWhite')` tre righe piu' sotto, che non dice ne'
   * quale variante ne' da dove arrivava. Meglio nominarla.
   */
  if (!config) {
    throw new Error(
      `createInitialBoard: variante sconosciuta ${JSON.stringify(variant)}. `
      + 'I valori di GameVariant sono MAIUSCOLI: converti con toGameVariant() '
      + 'se arrivi dalla forma minuscola usata sul filo.'
    );
  }
  const pieces: Piece[] = [];

  // Crea pezzi bianchi
  for (const square of config.initialWhite) {
    pieces.push({
      color: Color.WHITE,
      type: PieceType.MAN,
      square,
    });
  }

  // Crea pezzi neri
  for (const square of config.initialBlack) {
    pieces.push({
      color: Color.BLACK,
      type: PieceType.MAN,
      square,
    });
  }

  const board: BoardState = {
    pieces,
    currentTurn: config.blackStarts ? Color.BLACK : Color.WHITE,
    status: GameStatus.PLAYING,
    variant,
    ruleMode,
    moveHistory: [],
    halfMoveClock: 0,
    positionHistory: [],
  };
  /*
   * La posizione di PARTENZA entra subito nello storico.
   *
   * `positionHistory` veniva estesa solo dentro `applyMove`, quindi la posizione
   * iniziale non ci finiva mai: se era LEI a ripetersi tre volte, il conteggio
   * restava indietro di uno e la patta per ripetizione veniva dichiarata una
   * semi-mossa dopo, su una posizione diversa da quella che si era davvero
   * ripetuta. Nessuna patta persa, ma annunciata nel posto sbagliato — e in un
   * finale è la differenza fra capire perché è patta e non capirlo.
   */
  board.positionHistory = [serializePosition(board)];
  return board;
}

/**
 * Trova un pezzo su una casella
 * Ritorna undefined se la casella è vuota
 */
export function getPieceAt(board: BoardState, square: Square): Piece | undefined {
  return board.pieces.find((p) => p.square === square);
}

/**
 * Verifica se una casella è occupata
 */
export function isSquareOccupied(board: BoardState, square: Square): boolean {
  return getPieceAt(board, square) !== undefined;
}

/**
 * Verifica se una casella è occupata da un pezzo di un colore specifico
 */
export function isSquareOccupiedBy(board: BoardState, square: Square, color: Color): boolean {
  const piece = getPieceAt(board, square);
  return piece !== undefined && piece.color === color;
}

/**
 * Crea una copia profonda dello stato della scacchiera
 */
export function cloneBoard(board: BoardState): BoardState {
  return {
    pieces: board.pieces.map((p) => ({ ...p })),
    currentTurn: board.currentTurn,
    status: board.status,
    variant: board.variant,
    ruleMode: board.ruleMode,
    moveHistory: board.moveHistory.map((m) => ({
      ...m,
      captures: m.captures.map((c) => ({ ...c, piece: { ...c.piece } })),
    })),
    halfMoveClock: board.halfMoveClock,
    positionHistory: [...(board.positionHistory ?? [])],
    kingMoveStreak: board.kingMoveStreak
      ? {
          [Color.WHITE]: board.kingMoveStreak[Color.WHITE]
            ? { ...board.kingMoveStreak[Color.WHITE]! }
            : board.kingMoveStreak[Color.WHITE],
          [Color.BLACK]: board.kingMoveStreak[Color.BLACK]
            ? { ...board.kingMoveStreak[Color.BLACK]! }
            : board.kingMoveStreak[Color.BLACK],
        }
      : undefined,
    frisianEndgamePly: board.frisianEndgamePly,
  };
}

/**
 * Posiziona un pezzo sulla scacchiera (ritorna nuova board)
 * Se c'è già un pezzo, lo sostituisce
 */
export function setPieceAt(board: BoardState, piece: Piece): BoardState {
  const newBoard = cloneBoard(board);
  const existingIndex = newBoard.pieces.findIndex((p) => p.square === piece.square);
  
  if (existingIndex !== -1) {
    newBoard.pieces[existingIndex] = piece;
  } else {
    newBoard.pieces.push(piece);
  }
  
  return newBoard;
}

/**
 * Rimuove un pezzo dalla scacchiera (ritorna nuova board)
 * Ritorna la board invariata se non c'è nessun pezzo
 */
export function removePieceAt(board: BoardState, square: Square): BoardState {
  const newBoard = cloneBoard(board);
  newBoard.pieces = newBoard.pieces.filter((p) => p.square !== square);
  return newBoard;
}

/**
 * Conta i pezzi di un colore
 */
export function countPieces(board: BoardState, color: Color): number {
  return board.pieces.filter((p) => p.color === color).length;
}

/**
 * Conta le dame di un colore
 */
export function countKings(board: BoardState, color: Color): number {
  return board.pieces.filter((p) => p.color === color && p.type === PieceType.KING).length;
}

/**
 * Ritorna tutti i pezzi di un colore
 */
export function getPiecesByColor(board: BoardState, color: Color): Piece[] {
  return board.pieces.filter((p) => p.color === color);
}

/**
 * Cambia il turno
 */
export function switchTurn(board: BoardState): BoardState {
  const newBoard = cloneBoard(board);
  newBoard.currentTurn = board.currentTurn === Color.WHITE ? Color.BLACK : Color.WHITE;
  return newBoard;
}

/**
 * Ritorna il colore avversario
 */
export function getOpponent(color: Color): Color {
  return color === Color.WHITE ? Color.BLACK : Color.WHITE;
}
